"use client"

import React, { useState, useEffect, useCallback } from "react"
import Image from "next/image"
import { useRouter, useSearchParams } from "next/navigation"
import { useLang } from "hooks/useLang"

type FilterKey = null | "category" | "date" | "city" | "sort" | "price"

const FILTERS: {
    key: FilterKey
    label: string
    options?: string[]
}[] = [
    {
        key: "category",
        label: "category",
        options: ["activities", "festivals", "kids", "sport"],
    },
    {
        key: "date",
        label: "date",
        options: ["today", "tomorrow", "this_weekend", "this_month"],
    },
    {
        key: "city",
        label: "city",
        options: ["all", "jeddah", "riyadh", "dhahran", "mecca", "medina"],
    },
    {
        key: "sort",
        label: "sort",
        options: [
            "highest_price",
            "lowest_price",
            "close_to_me",
            "latest_first",
            "oldest_first",
        ],
    },
    { key: "price", label: "price" },
]

export default function EventFilteration() {
    const { t } = useLang()
    const router = useRouter()
    const searchParams = useSearchParams()

    const [activeFilter, setActiveFilter] = useState<FilterKey>(null)

    // Local state for instant UI (optimistic)
    const [queryParams, setQueryParams] = useState<Record<string, string>>({})

    /* -------------------------------- Sync URL → UI ------------------------------- */

    useEffect(() => {
        setQueryParams(Object.fromEntries(searchParams.entries()))
    }, [searchParams])

    /* -------------------------------- Helpers ----------------------------------- */

    const normalize = useCallback((v: string) => {
        return v.toLowerCase().replaceAll(" ", "_")
    }, [])

    const updateQuery = useCallback(
        (key: string, value: string) => {
            const normalizedValue = normalize(value)

            const params = new URLSearchParams(searchParams.toString())
            const currentValue = params.get(key)

            // ✅ TOGGLE OFF (same option clicked)
            if (currentValue === normalizedValue) {
                params.delete(key)
                router.replace(`?${params.toString()}`, { scroll: false })

                setQueryParams((prev) => {
                    const next = { ...prev }
                    delete next[key]
                    return next
                })

                if (activeFilter === key) setActiveFilter(null)
                return
            }

            // ✅ SET / CHANGE VALUE
            params.set(key, normalizedValue)
            router.replace(`?${params.toString()}`, { scroll: false })

            setQueryParams((prev) => ({
                ...prev,
                [key]: normalizedValue,
            }))
        },
        [searchParams, router, activeFilter, normalize]
    )

    const currentFilter = FILTERS.find((f) => f.key === activeFilter)
    const activeOptionForFilter = activeFilter
        ? queryParams[activeFilter]
        : null

    /* -------------------------------- Render ----------------------------------- */

    return (
        <div className="flex flex-col gap-4 w-full">
            <div className="w-full rounded-[20px] bg-white/20 backdrop-blur-md px-8 py-4 text-white">
                {/* Header */}
                <div className="flex flex-row justify-between">
                    <div className="flex items-center gap-2">
                        <Image
                            src="/svg/settings.svg"
                            alt="filters"
                            width={18}
                            height={18}
                            className="w-[18px] h-auto"
                        />
                        <span className="text-5 font-medium capitalize">
                            {t("filters")}
                        </span>
                    </div>

                    <div className="flex items-center gap-4">
                        {FILTERS.map((filter) => (
                            <button
                                key={filter.key}
                                onClick={() =>
                                    setActiveFilter((prev) =>
                                        prev === filter.key ? null : filter.key
                                    )
                                }
                                className={`capitalize rounded-full text-5 px-[10px] py-1 transition-all hover:opacity-80 ${
                                    activeFilter === filter.key
                                        ? "bg-[#1cbfa7] text-white"
                                        : "text-white/80 hover:text-white"
                                }`}
                            >
                                {t(filter.label)}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Active filter options */}
                {activeFilter && currentFilter?.options && (
                    <>
                        <div className="my-5 h-[1px] w-full bg-white/50" />

                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2 text-white/80 text-4">
                                {activeFilter === "date" && (
                                    <>
                                        <Image
                                            src="/svg/quistionmark.svg"
                                            alt="filters"
                                            width={18}
                                            height={18}
                                            className="w-[18px] h-auto"
                                        />
                                        <span className="text-4 capitalize">
                                            {t("filter_by_date_title")}
                                        </span>
                                    </>
                                )}
                            </div>

                            <div className="flex flex-wrap gap-4">
                                {currentFilter.options.map((option) => {
                                    const normalizedOption = normalize(option)

                                    return (
                                        <button
                                            key={option}
                                            onClick={() =>
                                                updateQuery(
                                                    activeFilter,
                                                    option
                                                )
                                            }
                                            className={`capitalize rounded-full text-5 px-[10px] py-1 transition-all hover:opacity-80 ${
                                                activeOptionForFilter ===
                                                normalizedOption
                                                    ? "bg-[#1cbfa7] text-white"
                                                    : "text-white/80 hover:text-white"
                                            }`}
                                        >
                                            {t(option)}
                                        </button>
                                    )
                                })}
                            </div>
                        </div>
                    </>
                )}
            </div>

            {/* Selected filters pills */}
            {Object.keys(queryParams).length > 0 && (
                <div className="mt-6 text-white/80 text-4 flex flex-wrap gap-3">
                    {Object.entries(queryParams).map(([key, value]) => (
                        <div
                            key={key}
                            className="flex flex-row items-center gap-2 px-4 py-1 rounded-full border border-[#19BF5E99] bg-[#19BF5E4D] backdrop-blur-md capitalize"
                        >
                            <span className="flex-1">
                                {t(value.replaceAll("_", " "))}
                            </span>

                            <button
                                onClick={() => {
                                    const params = new URLSearchParams(
                                        searchParams.toString()
                                    )
                                    params.delete(key)
                                    router.replace(`?${params.toString()}`, {
                                        scroll: false,
                                    })

                                    setQueryParams((prev) => {
                                        const next = { ...prev }
                                        delete next[key]
                                        return next
                                    })

                                    if (activeFilter === key)
                                        setActiveFilter(null)
                                }}
                            >
                                <Image
                                    src="/svg/whiteCross.svg"
                                    width={16}
                                    height={16}
                                    className="w-4 h-4 hover:opacity-60 transition-all"
                                    alt="remove"
                                />
                            </button>
                        </div>
                    ))}
                </div>
            )}
        </div>
    )
}
